/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Home type used to classify a marketplace listing. */
export type MarketplaceDwellingType =
  /** A condominium home. */
  | 'CONDO'
  /** A detached or single-family house. */
  | 'HOUSE'
  /** A property containing multiple residential units. */
  | 'MULTI_FAMILY'
  /** A dwelling type that does not fit the listed categories. */
  | 'OTHER'
  /** A townhouse. */
  | 'TOWNHOUSE';

/** Geographic coordinate in decimal degrees. */
export type MarketplaceGeoPointInput = {
  /** Latitude in decimal degrees, from -90 to 90. */
  lat: Types.Scalars['Latitude']['input'];
  /** Longitude in decimal degrees, from -180 to 180. */
  lon: Types.Scalars['Longitude']['input'];
};

/** Filters applied to marketplace listing results. */
export type MarketplaceListingFiltersInput = {
  /** When true, return homes expected to become available for purchase soon instead of homes currently on the market; with statusesAny, the homes on the market that have one of those statuses are returned too. Cannot be sent together with includeAvailableSoon. */
  availableSoon?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, return homes currently eligible for self-tour. */
  canSelfTour?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Return only homes whose dwelling type is one of these values. */
  dwellingTypes?: Array<MarketplaceDwellingType> | null | undefined;
  /** When true, return homes with a pool. */
  hasPool?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, include homes expected to become available for purchase soon in addition to homes currently on the market. Cannot be sent together with availableSoon. */
  includeAvailableSoon?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, also return homes under contract with a buyer. */
  includePending?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Maximum number of bedrooms. */
  maxBedrooms?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum monthly homeowners association dues, in US cents. */
  maxHoaMonthlyFeeCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum list price, in US cents. */
  maxListPriceCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum lot area in square feet. */
  maxLotSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum interior living area in square feet. */
  maxSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum full home area in square feet, including basement area when available. */
  maxTotalSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Latest allowed construction year. */
  maxYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum number of bathrooms; half bathrooms may be represented as .5. */
  minBathrooms?: Types.Scalars['Float']['input'] | null | undefined;
  /** Minimum number of bedrooms. */
  minBedrooms?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum list price, in US cents. */
  minListPriceCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum lot area in square feet. */
  minLotSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum interior living area in square feet. */
  minSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum full home area in square feet, including basement area when available. */
  minTotalSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Earliest allowed construction year. */
  minYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** When true, return only homes with a single-story layout. */
  singleStoryOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When set, the homes on the market in the results are only those with at least one of these statuses. Coming soon and pending homes that the other filters add are not narrowed. Repeated values count once. Must not be empty. */
  statusesAny?: Array<MarketplaceListingStatusFilter> | null | undefined;
};

/** Status of a home on the market that a marketplace search can select. */
export type MarketplaceListingStatusFilter =
  /** Homes on the market listed at most 7 whole days ago. Each match has the NEWLY_LISTED label. */
  | 'NEWLY_LISTED'
  /** Homes on the market whose list price is below the list price their current listing started at. Each match has the PRICE_DROP label. */
  | 'PRICE_DROP';

/** Rectangular map area in decimal degrees. A west value greater than east crosses the date line. */
export type MarketplaceMapBoundsInput = {
  /** Eastern longitude, from -180 to 180; it may be less than west across the date line. */
  east: Types.Scalars['Longitude']['input'];
  /** Northern latitude, from -90 to 90; must be greater than south. */
  north: Types.Scalars['Latitude']['input'];
  /** Southern latitude, from -90 to 90. */
  south: Types.Scalars['Latitude']['input'];
  /** Western longitude, from -180 to 180. */
  west: Types.Scalars['Longitude']['input'];
};

/** Area and home attributes used to compare listing prices. */
export type MarketplacePriceDistributionInput = {
  /** Area containing the homes to compare. */
  area: MarketplaceSearchAreaInput;
  /** Home attributes to match. Price limits are ignored so the full price range remains visible. */
  filters?: MarketplaceListingFiltersInput | null | undefined;
};

/** Geographic scope for listing search. Provide exactly one option. */
export type MarketplaceSearchAreaInput =
  {   /** Rectangular viewport, including one that crosses the date line. */
  bounds: MarketplaceMapBoundsInput; market?: never; vertices?: never; zoneIds?: never; }
  |  { bounds?: never;   /** Market identifier to search, up to 64 characters. */
  market: Types.Scalars['NonEmptyString']['input']; vertices?: never; zoneIds?: never; }
  |  { bounds?: never; market?: never;   /** Closed polygon defining the search area in decimal degrees; use 4 to 256 vertices. */
  vertices: Array<MarketplaceGeoPointInput>; zoneIds?: never; }
  |  { bounds?: never; market?: never; vertices?: never;   /** Up to 128 IDs of zones whose listings should be included. */
  zoneIds: Array<Types.Scalars['UUID']['input']>; };

export type MarketplacePriceDistributionQueryVariables = Exact<{
  input: Types.MarketplacePriceDistributionInput;
}>;


export type MarketplacePriceDistributionQuery = { marketplace: { priceDistribution: Array<{ minimumPriceCents: Types.Scalars['SafeInt']['output'], maximumPriceCents: Types.Scalars['SafeInt']['output'] | null, listingCount: Types.Scalars['SafeInt']['output'] }> | null } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const MarketplacePriceDistributionDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplacePriceDistribution($input: MarketplacePriceDistributionInput!) {
  marketplace {
    priceDistribution(input: $input) {
      minimumPriceCents
      maximumPriceCents
      listingCount
    }
  }
}
    `) as unknown as TypedDocumentString<MarketplacePriceDistributionQuery, MarketplacePriceDistributionQueryVariables>;